import { compensationApiBaseUrl, payrollConfigApiBaseUrl } from '../../shared/env';
import { createJsonClient } from '../../shared/httpClient';
import type { DeductionsGateway } from './DeductionsGateway';
import type {
  BulkAssignmentConfirm, BulkAssignmentPreview, BulkAssignmentRequest, BulkAssignmentResult, CatalogQuery, DeductionCatalogItem, DeductionDefinition,
  DeductionDefinitionInput, DeductionElection, DeductionSummary, GarnishmentRecord, LimitRule, ListQuery, Paged, TaxProfile, TaxProfileQuery,
  UpdateDeductionDefinitionInput,
} from './types';
import {
  buildQuery, deductionDefinitionBody, definitionToInput, legacyDefinitionBody, mapBulkPreview, mapBulkResult, mapDeductionCatalogItem, mapDeductionDefinition, mapDeductionElection,
  mapDeductionSummary, mapGarnishment, mapLimitRule, mapList, mapPaged, mapTaxProfile,
} from './wireMapping';

/** Definitions, catalog, tax profiles and limit rules are PayrollConfig (`/svc-payrollconfig`); elections, bulk assignment and garnishment records are
 * Compensation (`/svc-compensation`). Routes are exactly the contract's (docs/benefits-deductions-contract.md sections 4.1.3 and 4.2). */
export class HttpDeductionsGateway implements DeductionsGateway {
  private readonly config = createJsonClient(payrollConfigApiBaseUrl);
  private readonly compensation = createJsonClient(compensationApiBaseUrl);

  async listCatalog(query: CatalogQuery = {}, signal?: AbortSignal): Promise<DeductionCatalogItem[]> {
    const qs = buildQuery({ category: query.category, search: query.search, includeHidden: query.includeHidden ? true : undefined });
    return mapList(await this.config<unknown>(`/api/deduction-catalog${qs}`, undefined, signal), mapDeductionCatalogItem);
  }

  async hideCatalogType(code: string): Promise<void> {
    await this.config<unknown>(`/api/deduction-catalog/${encodeURIComponent(code)}/hide`, { method: 'POST' });
  }

  async showCatalogType(code: string): Promise<void> {
    await this.config<unknown>(`/api/deduction-catalog/${encodeURIComponent(code)}/show`, { method: 'POST' });
  }

  async listDefinitions(query: ListQuery = {}, signal?: AbortSignal): Promise<Paged<DeductionDefinition>> {
    const qs = buildQuery({
      search: query.search, category: query.category, status: query.status, effectiveOn: query.effectiveOn, sort: query.sort, page: query.page, pageSize: query.pageSize,
    });
    return mapPaged(await this.config<unknown>(`/api/deduction-definitions${qs}`, undefined, signal), mapDeductionDefinition);
  }

  async getDefinition(id: string, signal?: AbortSignal): Promise<DeductionDefinition> {
    return mapDeductionDefinition(await this.config<unknown>(`/api/deduction-codes/${encodeURIComponent(id)}`, undefined, signal));
  }

  async getSummary(signal?: AbortSignal): Promise<DeductionSummary> {
    return mapDeductionSummary(await this.config<unknown>('/api/deduction-definitions/summary', undefined, signal));
  }

  async createDefinition(input: DeductionDefinitionInput): Promise<DeductionDefinition> {
    return mapDeductionDefinition(await this.config<unknown>('/api/deduction-codes', { method: 'POST', body: JSON.stringify(deductionDefinitionBody(input)) }));
  }

  async updateDefinition(id: string, input: UpdateDeductionDefinitionInput): Promise<DeductionDefinition> {
    const body = { ...deductionDefinitionBody(input), expectedVersion: input.expectedVersion };
    return mapDeductionDefinition(await this.config<unknown>(`/api/deduction-codes/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(body) }));
  }

  async setDefinitionActive(definition: DeductionDefinition, isActive: boolean): Promise<DeductionDefinition> {
    if (definition.categoryCode === null) {
      return mapDeductionDefinition(await this.config<unknown>(`/api/deduction-codes/${encodeURIComponent(definition.id)}`, { method: 'PUT', body: JSON.stringify(legacyDefinitionBody(definition, isActive)) }));
    }
    const input = definitionToInput(definition, isActive);
    const body = { ...deductionDefinitionBody(input, definition), expectedVersion: definition.concurrencyVersion };
    return mapDeductionDefinition(await this.config<unknown>(`/api/deduction-codes/${encodeURIComponent(definition.id)}`, { method: 'PUT', body: JSON.stringify(body) }));
  }

  async listTaxProfiles(query: TaxProfileQuery = {}, signal?: AbortSignal): Promise<TaxProfile[]> {
    const qs = buildQuery({ asOf: query.asOf, includeUnapproved: query.includeUnapproved ? true : undefined });
    return mapList(await this.config<unknown>(`/api/deduction-tax-profiles${qs}`, undefined, signal), mapTaxProfile);
  }

  async getTaxProfile(code: string, signal?: AbortSignal): Promise<TaxProfile> {
    return mapTaxProfile(await this.config<unknown>(`/api/deduction-tax-profiles/${encodeURIComponent(code)}`, undefined, signal));
  }

  async listLimitRules(query: TaxProfileQuery = {}, signal?: AbortSignal): Promise<LimitRule[]> {
    const qs = buildQuery({ asOf: query.asOf, includeUnapproved: query.includeUnapproved ? true : undefined });
    return mapList(await this.config<unknown>(`/api/deduction-limit-rules${qs}`, undefined, signal), mapLimitRule);
  }

  async listCompanyElections(signal?: AbortSignal): Promise<DeductionElection[]> {
    return mapList(await this.compensation<unknown>('/api/deduction-elections', undefined, signal), mapDeductionElection);
  }

  async previewBulkAssignment(request: BulkAssignmentRequest): Promise<BulkAssignmentPreview> {
    return mapBulkPreview(await this.compensation<unknown>('/api/deduction-assignments/preview', { method: 'POST', body: JSON.stringify(bulkBody(request)) }));
  }

  async confirmBulkAssignment(body: BulkAssignmentConfirm): Promise<BulkAssignmentResult> {
    const payload = { ...bulkBody(body), idempotencyKey: body.idempotencyKey, confirm: true };
    return mapBulkResult(await this.compensation<unknown>('/api/deduction-assignments', { method: 'POST', body: JSON.stringify(payload) }));
  }

  async listGarnishments(employeeId: string, signal?: AbortSignal): Promise<GarnishmentRecord[]> {
    return mapList(await this.compensation<unknown>(`/api/employees/${encodeURIComponent(employeeId)}/garnishments`, undefined, signal), mapGarnishment);
  }
}

function bulkBody(request: BulkAssignmentRequest): Record<string, unknown> {
  return {
    deductionCodeId: request.deductionCodeId,
    scope: request.scope,
    ...(request.scope === 'Employees' ? { employeeIds: request.employeeIds ?? [] } : {}),
    effectiveStartDate: request.effectiveStartDate,
    amountOverride: request.amountOverride ?? null,
    companyContributionOverride: request.companyContributionOverride ?? null,
  };
}
